/**
 * Lesson Viewer — editorial reading layout with Phase 4 quiz controls
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import AuraCharacter from '../common/AuraCharacter';
import NoteEditor from '../notes/NoteEditor';
import HeaderNav from '../HeaderNav';
import './Learning.css';

const PAGE_MOTION = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: 'easeOut' },
};

const LessonViewer = ({ user, onLogout }) => {
  const { pathId, lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [path, setPath] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(null);
  const [error, setError] = useState(null);
  const [showNoteEditor, setShowNoteEditor] = useState(false);

  useEffect(() => {
    loadLesson();
  }, [pathId, lessonId]);

  const loadLesson = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');

      const pathRes = await axios.get(`/api/learning/progress/paths/${pathId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPath(pathRes.data.path);

      const lessonRes = await axios.get(
        `/api/learning/progress/paths/${pathId}/lessons/${lessonId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLesson(lessonRes.data.lesson);

      if (user?.user_id) {
        const progressRes = await axios.get(
          `/api/learning/progress/user/${user.user_id}/progress/${pathId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setProgress(progressRes.data.progress);
      }
    } catch (err) {
      console.error('Error loading lesson:', err);
      setError('Failed to load lesson');
    } finally {
      setLoading(false);
    }
  };

  const handleQuizSubmit = async () => {
    if (!lesson?.quiz || quizSubmitted) return;

    let correct = 0;
    lesson.quiz.forEach((q, idx) => {
      if (quizAnswers[idx] === q.correct) correct++;
    });
    const score = (correct / lesson.quiz.length) * 100;
    setQuizScore(score);
    setQuizSubmitted(true);

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      await axios.post(
        `/api/learning/progress/user/${user.user_id}/paths/${pathId}/lessons/${lessonId}/complete`,
        { quiz_score: score },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error('Error completing lesson:', err);
    }
  };

  const handleAnswerSelect = (questionIdx, answerIdx) => {
    if (quizSubmitted) return;
    setQuizAnswers({ ...quizAnswers, [questionIdx]: answerIdx });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <p className="text-body text-secondary">Loading lesson…</p>
      </div>
    );
  }

  if (error || !lesson) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center p-6">
        <div className="surface-panel p-6 max-w-md border border-danger/40">
          <p className="text-body text-danger font-medium mb-2">Error</p>
          <p className="text-body text-secondary">{error || 'Lesson not found'}</p>
          <button
            type="button"
            onClick={() => navigate(`/learning/paths/${pathId}`)}
            className="btn-secondary mt-4"
          >
            Back to path
          </button>
        </div>
      </div>
    );
  }

  const isCompleted = progress?.completed_lessons?.includes(lessonId);

  return (
    <div className="min-h-screen bg-bone">
      {user && (
        <HeaderNav
          activeSection="learn-and-play"
          onSectionChange={() => {}}
          user={user}
          onLogout={onLogout}
          visionCount={0}
        />
      )}
      <div className="max-w-3xl mx-auto px-6 py-8">
        <motion.header {...PAGE_MOTION} className="mb-10 pb-8 border-b border-hairline">
          <button
            type="button"
            onClick={() => navigate(`/learning/paths/${pathId}`)}
            className="flex items-center gap-2 text-secondary hover:text-primary mb-6 transition-colors duration-300"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <span className="text-label">Back to {path?.name || 'path'}</span>
          </button>
          <h1 className="font-display text-h1 text-primary mb-3">{lesson.title}</h1>
          {isCompleted && (
            <span className="badge-mark badge-mark-earned">Completed</span>
          )}
        </motion.header>

        <motion.article
          {...PAGE_MOTION}
          transition={{ ...PAGE_MOTION.transition, delay: 0.05 }}
          className="relative mb-10"
        >
          <button
            type="button"
            onClick={() => setShowNoteEditor(true)}
            className="btn-ghost text-label absolute top-0 right-0"
          >
            Save note
          </button>
          <div className="prose-editorial mx-auto pt-8">
            <div className="whitespace-pre-wrap">
              {lesson.content.split('\n').map((line, idx) => {
                const trimmed = line.trim();
                if (trimmed.startsWith('# ')) {
                  return (
                    <h2 key={idx} className="font-display text-h2 text-primary mt-8 mb-4 first:mt-0">
                      {trimmed.replace(/^#+\s*/, '')}
                    </h2>
                  );
                }
                if (trimmed.startsWith('## ')) {
                  return (
                    <h3 key={idx} className="font-display text-h3 text-primary mt-6 mb-3">
                      {trimmed.replace(/^#+\s*/, '')}
                    </h3>
                  );
                }
                if (trimmed.startsWith('### ')) {
                  return (
                    <h4 key={idx} className="font-sans text-h3 text-primary mt-4 mb-2">
                      {trimmed.replace(/^#+\s*/, '')}
                    </h4>
                  );
                }
                if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
                  return (
                    <p key={idx} className="font-medium text-primary my-2">
                      {trimmed.replace(/\*\*/g, '')}
                    </p>
                  );
                }
                if (!trimmed) return <br key={idx} />;
                return (
                  <p key={idx} className="text-body text-primary my-2">
                    {line}
                  </p>
                );
              })}
            </div>
          </div>
        </motion.article>

        {lesson.quiz?.length > 0 && (
          <motion.section
            {...PAGE_MOTION}
            transition={{ ...PAGE_MOTION.transition, delay: 0.1 }}
            className="max-w-prose mx-auto mb-10"
          >
            <h2 className="font-display text-h2 text-primary mb-6">Quick quiz</h2>
            {lesson.quiz.map((question, qIdx) => (
              <div key={qIdx} className="mb-8 pb-8 border-b border-hairline last:border-0">
                <p className="text-body font-medium text-primary mb-4">{question.question}</p>
                <div className="space-y-2">
                  {question.options.map((option, oIdx) => {
                    const isSelected = quizAnswers[qIdx] === oIdx;
                    const isCorrect = oIdx === question.correct;
                    const showResult = quizSubmitted;

                    let cls = 'quiz-option';
                    if (showResult && isCorrect) cls += ' quiz-option-correct';
                    else if (showResult && isSelected && !isCorrect) cls += ' quiz-option-incorrect';
                    else if (!showResult && isSelected) cls += ' quiz-option-selected';

                    return (
                      <button
                        key={oIdx}
                        type="button"
                        onClick={() => handleAnswerSelect(qIdx, oIdx)}
                        disabled={quizSubmitted}
                        className={cls}
                      >
                        <span className="flex items-center gap-3">
                          <input
                            type="radio"
                            readOnly
                            checked={isSelected}
                            className="radio-editorial pointer-events-none"
                            tabIndex={-1}
                          />
                          {option}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {!quizSubmitted ? (
              <button
                type="button"
                onClick={handleQuizSubmit}
                disabled={Object.keys(quizAnswers).length < lesson.quiz.length}
                className="btn-primary w-full"
              >
                Submit quiz
              </button>
            ) : (
              <div className="surface-panel p-5">
                <p className="figure font-display text-h2 text-primary tabular-nums mb-2">
                  {Math.round(quizScore)}%
                </p>
                <p className="text-body text-secondary">
                  {quizScore >= 80
                    ? 'Great job — you understand the key concepts.'
                    : 'Good effort — review the content if you need clarification.'}
                </p>
              </div>
            )}
          </motion.section>
        )}

        <div className="flex justify-between gap-4 max-w-prose mx-auto">
          <button type="button" onClick={() => navigate(-1)} className="btn-ghost">
            Previous
          </button>
          <button
            type="button"
            onClick={() => navigate(`/learning/paths/${pathId}`)}
            className="btn-secondary"
          >
            Back to path
          </button>
        </div>
      </div>

      <AuraCharacter
        context="learning"
        initialMessage="Take your time reading through the content. The quiz helps reinforce key concepts."
        position="bottom-right"
        compact={false}
      />

      {showNoteEditor && (
        <NoteEditor
          user={user}
          linkedType="learning_lesson"
          linkedId={lessonId}
          onClose={() => setShowNoteEditor(false)}
          onSave={() => setShowNoteEditor(false)}
        />
      )}
    </div>
  );
};

export default LessonViewer;
